import { useTranslation } from "react-i18next";
import { Client } from "../../../models/Client";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../state/store";
import { useEffect, useMemo, useState } from "react";
import { Toast } from "../../Toast";
import { deleteFollowUpMessageExcecution, fetchClientAutomations, needToLoadClientAutomation } from "../../../state/automations/action";
import { longLocalDateFormat, shortDateFormat } from "../../../utils/functions";
import Button from "../../Button";
import FilteredTable from "../../FilteredTable";
import { toast, ToastContainer } from "react-toastify";
import { LoadingMask } from "../../LoadingMask";
import { PauseIcon } from "@heroicons/react/24/solid";
import { ClientLeadFrequencyAutomation } from "../../../models/FollowUp";
import { DeactivateClientAutomationModal } from "./DeactivateClientAutomationModal";

interface Props {
    client: Client | null
    canEditClient: boolean
}

export const ClientAutomationTable = (props: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const [automations, setAutomations] = useState<any[]>([]);
    const clientAutomations = useSelector((state: RootState) => state.automations.clientAutomations);
    const isLoadingAutomations = useSelector((state: RootState) => state.automations.isLoadingClientLeadAutomations);
    const needLoadClientAutomations = useSelector((state: RootState) => state.automations.needToLoadClientAutomations);
    const error = useSelector((state: any) => state.automations.errorClientLeadAutomations);
    const [showAutomationError, setShowAutomationError] = useState(false);

    const [ showDeativateClientAutomationModal, setShowDeativateClientAutomationModal] = useState<{
        automation?: any;
        show: boolean;
    }>({ show: false, automation: undefined });

    useEffect(() => {
        if (showAutomationError) {
            Toast(
                t(error?.reason ?? "SOMETHING_WENT_WRONG"),
                t(error?.cause_info)
            );
        }
    }, [showAutomationError, error, t]);

    useEffect(() => {
        //TODO: Review state and get data
        if(needLoadClientAutomations) {
            dispatch(fetchClientAutomations(props.client?.id!)).then((e) => {
                let automations = (e.payload || []) as ClientLeadFrequencyAutomation[];
                setAutomations(automations);
            });
            dispatch(needToLoadClientAutomation(false));
        } else {
            setAutomations(clientAutomations);
        }
    }, [clientAutomations, needLoadClientAutomations]);

    const deactivateAutomation = (automation: ClientLeadFrequencyAutomation) => {
        //TODO: remove address from database
        dispatch(deleteFollowUpMessageExcecution({ followUpXClientLeadId: automation.id, isClient: true })).then((e) => {
            if (e.type === "followUp/deleteFollowUpMessageExcecution/rejected") {
                setShowAutomationError(true);
            } else {
                toast(t("SUCCESSFULLY_DEACTIVATE_FOLLOW_UP"));
            }
        });
    }

    const columns = useMemo(() => {
        const showColumns = [
            {
                Header: t("DESCRIPTION"),
                Cell: (row: any) => row.row.original.followup_message.description,
            },
            {
                Header: t('FREQUENCY'),
                Cell: ( row: any ) => {
                    const { frequency } = row.row.original.followup_message;
                    return frequency ? frequency : "-";
                },
            },
            {
                Header: t('FREQUENCY_UNIT'),
                Cell: ( row: any ) => {
                    const { frequency_unit } = row.row.original.followup_message;
                    return frequency_unit ? frequency_unit === "day" ? t("DAY") : t("MONTH") : "-";
                },
            },
            {
                Header: t('FREQUENCY_TIME'),
                Cell: ( row: any ) => {
                    const { frequency_time } = row.row.original.followup_message;
                    return frequency_time ? frequency_time : "-";
                },
            },
            {
                Header: t('SPECIFIC_DATE'),
                Cell: ( row: any ) => {
                    const { specific_date } = row.row.original.followup_message;
                    return specific_date ? longLocalDateFormat(specific_date) : "-";
                },
            },
            {
                Header: t('SPECIFIC_DATE_RECURRENT'),
                Cell: ( row: any ) => {
                    const { is_specific_date_recurrent } = row.row.original.followup_message;
                    return is_specific_date_recurrent ? is_specific_date_recurrent === '0' ? t("NO") : t("YES") : "-";
                },
            },
            {
                Header: t("NEXT_EXECUTION"),
                Cell: (row: any) => row.row.original.next_execution ? `${longLocalDateFormat(row.row.original.next_execution)}` : "-",
            }
        ]

        const actions = {
            id: "actions",
            Header: "",
            Cell: (row: any) => {
                return (
                    <div className="flex flex-row justify-center divide-x">
                        <Button
                            isTerciary
                            iconOnly
                            tooltip={t("DEACTIVATE_AUTOMATION")}
                            onClick={() => setShowDeativateClientAutomationModal({ show: true, automation: row.row.original })}
                            classNames="mr-2"
                        >
                            <PauseIcon className="h-5 aspect-square text-red-600" />
                        </Button>
                    </div>
                );
            },
        }

        if(!props.canEditClient) {
            return [...showColumns]
        }
        
        return [...showColumns, actions]
    }, [deactivateAutomation, t]);

    return (
        <>
            <FilteredTable
                columns={columns}
                data={automations}
                hasSearchBar={false}
            />
            <DeactivateClientAutomationModal
              info={showDeativateClientAutomationModal}
              onClose={() => setShowDeativateClientAutomationModal({show : false, automation: undefined})}
              deactivateAutomation={(automation: ClientLeadFrequencyAutomation) => deactivateAutomation(automation)}
            />
            <ToastContainer progressStyle={{ background: "#D4AF37" }} />
            { isLoadingAutomations && <LoadingMask />}
        </>
    )
}